// src/components/TeamSelectAutocomplete.tsx
import { useMemo } from 'react';
import { Box, HStack, Image, InputGroup, Text } from '@chakra-ui/react';
import { Select, chakraComponents } from 'chakra-react-select';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import type { Club } from '@/model';
import { useTranslation } from 'react-i18next';


type BaseProps = {
  clubs: Club[];
  width?: any;
  showFlag?: boolean;
  flagSize?: number;
};

/** Props genéricas: qualquer form e qualquer nome de campo string do form */
export type TeamSelectAutocompleteProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = BaseProps & {
  control: Control<TFieldValues>;
  name: TName; // ex.: 'team_uuid' | 'filters.team_uuid' | 'clubId'
};

/** Componente genérico */
export default function TeamSelectAutocomplete<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  clubs,
  width = '100%',
  showFlag = false,
  flagSize = 18,
}: TeamSelectAutocompleteProps<TFieldValues, TName>) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      clubs.map(c => ({
        value: c.uuid,
        label: c.name,
        logo: c.logo_path,
      })),
    [clubs],
  );

  const allLabel = t('team_autocomplete.team_select_all') as string;

  const FlagOption = (props: any) => {
    const { data } = props;
    return (
      <chakraComponents.Option {...props}>
        <HStack spacing={2}>
          {data.logo ? (
            <Image alt={data.label} src={data.logo} boxSize={`${flagSize}px`} borderRadius='sm' objectFit='contain' />
          ) : null}
          <Text noOfLines={1}>{data.label}</Text>
        </HStack>
      </chakraComponents.Option>
    );
  };

  const FlagSingleValue = (props: any) => {
    const { data } = props;
    return (
      <chakraComponents.SingleValue {...props}>
        <HStack spacing={2}>
          {data.logo ? (
            <Image alt={data.label} src={data.logo} boxSize={`${flagSize}px`} borderRadius='sm' objectFit='contain' />
          ) : null}
          <Text noOfLines={1}>{data.label}</Text>
        </HStack>
      </chakraComponents.SingleValue>
    );
  };

  const baseComponents = { IndicatorSeparator: () => null };
  const components = showFlag ? { ...baseComponents, Option: FlagOption, SingleValue: FlagSingleValue } : baseComponents;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selected = options.find(o => o.value === (field.value as string)) ?? null;

        return (
          <Box width={width}>
            <InputGroup display='flex' flexDirection='column'>
              <Text fontWeight='medium'>
                {t('team_autocomplete.team_label')}
              </Text>

              <Select
                size='md'
                isClearable
                placeholder={allLabel}
                options={options}
                value={selected}
                onChange={opt => field.onChange((opt as any)?.value ?? '')}
                onBlur={field.onBlur}
                menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                menuPosition='fixed'
                components={components}
                chakraStyles={{
                  container: p => ({ ...p, w: '100%' }),
                  control: (p, state) => ({
                    ...p,
                    bg: 'white',
                    border: 'none',
                    borderBottom: '1px solid',
                    borderColor: state.isFocused ? 'brand.500' : 'gray.300',
                    rounded: 0,
                    boxShadow: 'none !important',
                    minH: '32px',
                    h: '32px',
                    px: 0,
                    _hover: { borderColor: 'gray.400' },
                  }),
                  valueContainer: p => ({ ...p, px: 0 }),
                  inputContainer: p => ({ ...p, px: 0 }),
                  indicatorsContainer: p => ({ ...p, pr: 0 }),
                  placeholder: p => ({ ...p, color: 'gray.500' }),
                  singleValue: p => ({ ...p, color: 'gray.800' }),
                  menuPortal: p => ({ ...p, zIndex: 1600 }),
                  menu: p => ({
                    ...p,
                    mt: 1,
                    bg: 'white',
                    borderRadius: 'md',
                    boxShadow: 'lg',
                  }),
                  menuList: p => ({ ...p, maxH: '280px', py: 1 }),
                  option: (p, state) => ({
                    ...p,
                    background: state.isSelected ? 'gray.100' : state.isFocused ? 'gray.100' : '#FFFFFF',
                    color: 'gray.800',
                    cursor: 'pointer',
                    _active: { bg: 'gray.200' },
                  }),
                  dropdownIndicator: (p, state) => ({
                    ...p,
                    color: state.isFocused ? 'brand.500' : 'gray.500',
                    background: '#FFFFFF',
                    px: 1,
                  }),
                  clearIndicator: p => ({
                    ...p,
                    color: 'gray.500',
                    px: 1,
                    _hover: { color: 'gray.700' },
                  }),
                }}
              />
            </InputGroup>
          </Box>
        );
      }}
    />
  );
}
